import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  Subject,
  timer,
  EMPTY,
  of,
} from 'rxjs';
import { switchMap, tap, catchError, takeUntil } from 'rxjs/operators';
import { AppConfigService } from '../../../config/app-config.service';
import { AppEndpoints } from '../../constants/app_endpoints';
import { ApiResponse } from '../../models/api-response';
import {
  NotificationItem,
  NotificationPagedResult,
  NotificationQueryParams,
  NotificationUnreadCount,
} from '../../models/notifications/notification.models';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private readonly baseUrl: string;
  /** Default polling interval for unread-count refresh (ms). */
  private readonly DEFAULT_POLL_INTERVAL_MS = 60_000;

  private _unreadCount$ = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this._unreadCount$.asObservable();

  private pollSubscription?: Subscription;
  private stopPoll$ = new Subject<void>();
  private currentChannel?: string;
  private visibilityHandler?: () => void;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.baseUrl = this.appConfigService.getApiGatewayBaseUrl();
  }

  ngOnDestroy(): void {
    this.stopUnreadCountPolling();
  }

  setUnreadCount(count: number): void {
    this._unreadCount$.next(count);
  }

  /**
   * Starts polling the unread notification count at a fixed interval.
   * Emits immediately, then every `intervalMs` (default 60s).
   * Safe to call multiple times — any previous poll is cancelled first.
   */
  startUnreadCountPolling(
    channel: string = 'WEB',
    intervalMs: number = this.DEFAULT_POLL_INTERVAL_MS
  ): void {
    this.stopUnreadCountPolling();
    this.currentChannel = channel;

    this.pollSubscription = timer(0, intervalMs)
      .pipe(
        takeUntil(this.stopPoll$),
        switchMap(() =>
          this.getUnreadCount(channel).pipe(
            catchError(() => EMPTY) // swallow transient errors, keep polling
          )
        )
      )
      .subscribe();

    // Refresh immediately when the tab becomes visible again so the
    // badge catches up after the browser throttles background timers.
    if (typeof document !== 'undefined' && !this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible' && this.currentChannel) {
          this.getUnreadCount(this.currentChannel)
            .pipe(catchError(() => EMPTY))
            .subscribe();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  /** Stops the unread-count polling and clears visibility listener. */
  stopUnreadCountPolling(): void {
    this.stopPoll$.next();
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
    this.currentChannel = undefined;
    if (typeof document !== 'undefined' && this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
  }

  /** Force an immediate unread-count refresh (e.g. after mark as read). */
  refreshUnreadCount(channel: string = this.currentChannel ?? 'WEB'): void {
    this.getUnreadCount(channel).pipe(catchError(() => EMPTY)).subscribe();
  }

  getNotifications(
    params: NotificationQueryParams = {}
  ): Observable<ApiResponse<NotificationPagedResult>> {
    const cleanParams: Record<string, string | number | boolean> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        cleanParams[key] = value as string | number | boolean;
      }
    });
    const url = `${this.baseUrl}${AppEndpoints.notifications.getAll(cleanParams)}`;
    return this.http.get<ApiResponse<NotificationPagedResult>>(url).pipe(
      catchError(() =>
        of({
          success: false,
          message: 'Notifications unavailable',
          data: {
            items: [],
            totalCount: 0,
            pageNumber: params.pageNumber ?? 1,
            pageSize: params.pageSize ?? 20,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        } as ApiResponse<NotificationPagedResult>)
      )
    );
  }

  getUnreadCount(
    channel?: string
  ): Observable<ApiResponse<NotificationUnreadCount>> {
    const url = `${this.baseUrl}${AppEndpoints.notifications.getUnreadCount(channel)}`;
    return this.http.get<ApiResponse<NotificationUnreadCount>>(url).pipe(
      tap((res) => {
        const data = res?.data as NotificationUnreadCount | undefined;
        if (data) {
          const count =
            typeof data.unreadCount === 'number'
              ? data.unreadCount
              : typeof data.webCount === 'number'
              ? data.webCount
              : typeof data.totalCount === 'number'
              ? data.totalCount
              : 0;
          this._unreadCount$.next(count);
        }
      }),
      catchError(() =>
        of({
          success: false,
          message: 'Notifications unavailable',
          data: { unreadCount: 0 },
        } as ApiResponse<NotificationUnreadCount>)
      )
    );
  }

  markAsRead(id: number): Observable<ApiResponse<boolean>> {
    const url = `${this.baseUrl}${AppEndpoints.notifications.markAsRead(id)}`;
    return this.http.patch<ApiResponse<boolean>>(url, null);
  }

  clearNotification(id: number): Observable<ApiResponse<boolean>> {
    const url = `${this.baseUrl}${AppEndpoints.notifications.clear(id)}`;
    return this.http.patch<ApiResponse<boolean>>(url, null);
  }

  readAll(channel?: string): Observable<ApiResponse<number>> {
    const url = `${this.baseUrl}${AppEndpoints.notifications.readAll(channel)}`;
    return this.http.patch<ApiResponse<number>>(url, null);
  }

  clearAll(channel?: string): Observable<ApiResponse<number>> {
    const url = `${this.baseUrl}${AppEndpoints.notifications.clearAll(channel)}`;
    return this.http.patch<ApiResponse<number>>(url, null);
  }
}
